import React from "react";
import { Link, useNavigate } from "react-router";
import {
  Link as MuiLink,
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  useMediaQuery,
  Theme,
  IconButton,
  Box,
  BoxProps,
  Skeleton,
} from "@mui/material";
import styled from "@emotion/styled";
import ArrowLeft from "@icons/myicons/arrow-left.svg?react";

const BreadcrumbsContainer = styled(Box)`
  display: flex;
  align-items: center;
  margin: ${({ theme }) => theme.spacing(8, 0, 5, 6)};

  ${({ theme }) => theme.breakpoints.down("sm")} {
    margin: ${({ theme }) => theme.spacing(3, 0, 4, 3)};
  }
`;

const StyledIconButton = styled(IconButton)`
  padding: 0;
  height: 25px;
  width: 25px;
  margin-right: ${({ theme }) => theme.spacing(2)};
  margin-left: ${({ theme }) => theme.spacing(1.5)};
  border-radius: 50%;
`;

const ArrowRight = styled(ArrowLeft)`
  transform: rotate(180deg);
`;

export interface BreadcrumbsProps extends BoxProps {
  backUrl?: string;
  items: BreadcrumbItem[];
  fontSize?: string;
  showBackButton?: boolean;
}

export const Breadcrumbs = (props: BreadcrumbsProps) => {
  const { items, backUrl, showBackButton = true, ...rest } = props;

  const navigate = useNavigate();
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const lastIndexItems = items.length - 1;
  const breadcrumbsAmount = items.length;
  const breadrcumbsLength = items.reduce(
    (acc, item) => item.text.length + acc,
    0
  );

  return (
    <BreadcrumbsContainer aria-label="breadcrumb" {...rest}>
      {showBackButton && (
        <StyledIconButton
          color="tertiary"
          onClick={() =>
            navigate(backUrl || items[items.length - 2]?.to || "/")
          }
        >
          <ArrowLeft width="20px" height="20px" />
        </StyledIconButton>
      )}
      <MuiBreadcrumbs
        separator={<ArrowRight width="20px" height="20px" />}
        sx={{
          "& li": {
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        {items.map((item, index) => {
          const key = `${item.text}-${item.to}`;
          const shouldDisplayDots =
            isXs &&
            (breadrcumbsLength > 40 || breadcrumbsAmount > 3) &&
            index === 0 &&
            item.to === "/";

          if (item.isFetching) {
            return <Skeleton width={100} key={key} />;
          }

          if (item.href) {
            return (
              <MuiLink
                key={key}
                href={item.href}
                color="inherit"
                variant="body3"
                underline="hover"
              >
                {shouldDisplayDots ? "..." : item.text}
              </MuiLink>
            );
          }

          if (!item.to) {
            return (
              <Typography
                key={key}
                color={
                  lastIndexItems === index ? "textPrimary" : "textSecondary"
                }
                variant="body3"
              >
                {shouldDisplayDots ? "..." : item.text}
              </Typography>
            );
          }

          return (
            <MuiLink
              key={key}
              color="inherit"
              to={item.to ?? ""}
              component={Link}
              variant="body3"
              underline="hover"
            >
              {shouldDisplayDots ? "..." : item.text}
            </MuiLink>
          );
        })}
      </MuiBreadcrumbs>
    </BreadcrumbsContainer>
  );
};
